import { useState, useMemo } from 'react';

interface AttributeAccordionProps {
  attributeNames: string[];
  selectedAttributes: string[];
  onAttributeSelection: (attributeName: string) => void;
  masterData: any;
}

export default function AttributeAccordion({
  attributeNames,
  selectedAttributes,
  onAttributeSelection,
  masterData
}: AttributeAccordionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const attributeGroups = {
    'Power': [
      'AC adapter power',
      'Charging port type',
      'USB-C PD',
      'USB-C Voltage',
      'AC input voltage',
      'Legacy AC adapter tip size',
      'Legacy Power Tip'
    ],
    'Display': [
      'Display diagonal',
      'Display resolution',
      'ASPECT RATIO',
      'Touchscreen'
    ],
    'Video Ports': [
      'DisplayPorts quantity',
      'HDMI ports quantity',
      'DVI port'
    ],
    'Cable Locks': [
      'Cable lock slot',
      'Cable lock slot type (1)',
      'Cable lock slot type (2)',
      'Universal'
    ],
    'Type-A ports': [
      'Number of USB-A Ports',
      'USB 3.2 GEN 2 (3.1 GEN 2) TYPE-A PORTS QUANTITY',
      'USB 3.2 Gen 1 (3.1 Gen 1) Type-A ports quantity ',
      'USB 2.0 Type-A Ports Quantity',
      'USB 2.0 ports quantity'
    ],
    'Type-C Ports': [
      'Number of USB-C Ports',
      'USB 2.0 Type-C Ports',
      'USB 3.2 GEN 1 (3.1 GEN 1) TYPE-C PORTS QUANTITY',
      'USB 3.2 Gen 2 (3.1 Gen 2) Type-C ports quantity',
      'DisplayPort Alt Mode Type-C Ports',
      'Thunderbolt 3 Type-C Ports',
      'Thunderbolt 4 Type-C Ports',
      'USB4 Type-C ports',
      'Thunderbolt 5 Type C Ports'
    ],
    'Audio Ports': [
      'Combo headphone/mic port'
    ],
    'Communication': [
      'Ethernet LAN (RJ-45) ports',
      'Wi-Fi',
      'Wi-Fi standards',
      'Bluetooth'
    ],
    'Dimensions': [
      'Width',
      'Depth',
      'Height',
      'Weight'
    ]
  };

  // Group attributes and filter only available ones
  const groupedAttributes = useMemo(() => {
    const uniqueAttributes = Array.from(new Set(attributeNames));
    const grouped: { [key: string]: string[] } = {};
    const ungrouped: string[] = [];

    uniqueAttributes.forEach(attr => {
      let isGrouped = false;
      for (const [groupName, groupAttrs] of Object.entries(attributeGroups)) {
        if (groupAttrs.includes(attr)) {
          if (!grouped[groupName]) {
            grouped[groupName] = [];
          }
          grouped[groupName].push(attr);
          isGrouped = true;
          break;
        }
      }
      if (!isGrouped) {
        ungrouped.push(attr);
      }
    });

    // Sort attributes within each group
    Object.keys(grouped).forEach(groupName => {
      grouped[groupName].sort();
    });

    return { grouped, ungrouped: ungrouped.sort() };
  }, [attributeNames]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectAll = () => {
    const allAttributes = [
      ...Object.values(groupedAttributes.grouped).flat(),
      ...groupedAttributes.ungrouped
    ];
    
    if (selectedAttributes.length === allAttributes.length) {
      // If all are selected, deselect all
      allAttributes.forEach(attr => {
        if (selectedAttributes.includes(attr)) {
          onAttributeSelection(attr);
        }
      });
    } else {
      // Select all attributes that aren't already selected
      allAttributes.forEach(attr => {
        if (!selectedAttributes.includes(attr)) {
          onAttributeSelection(attr);
        }
      });
    }
  };

  const renderAttributeCheckbox = (attrName: string, index: number) => (
    <div
      key={`${attrName}-${index}`}
      className="d-flex align-items-center gap-2 p-2"
      style={{ 
        minHeight: '35px',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
        margin: '4px 0'
      }}
    >
      <input
        type="checkbox"
        style={{ width: "16px", height: "16px", flexShrink: 0 }}
        id={`attr-${attrName}-${index}`}
        checked={selectedAttributes.includes(attrName)}
        onChange={() => onAttributeSelection(attrName)}
      />
      <label
        htmlFor={`attr-${attrName}-${index}`}
        className="mb-0"
        style={{ cursor: 'pointer', flex: 1, fontSize: '14px' }}
        title={attrName}
      >
        {attrName}
      </label>
    </div>
  );

  return (
    <div className="mt-4">
      <div
        className="d-flex align-items-center justify-content-between p-3 border rounded cursor-pointer"
        onClick={toggleAccordion}
        style={{ backgroundColor: '#f8f9fa' }}
      >
        <h6 className="mb-0 fw-bold text-primary">
          {selectedAttributes.length > 0
            ? `Selected Attributes (${selectedAttributes.length})`
            : 'Select Attributes'}
        </h6>
        <span className="ms-2">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

      {isOpen && (
        <div className="border border-top-0 rounded-bottom">
          {Object.keys(groupedAttributes.grouped).length > 0 || groupedAttributes.ungrouped.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '12px' }}>
              <div className="mb-3">
                <div
                  className="d-flex align-items-center float-end gap-2 p-2"
                  style={{ 
                   
                    margin: '0px 0'
                  }}
                >
                  <input
                    type="checkbox"
                    style={{ width: "16px", height: "16px", flexShrink: 0 }}
                    id="select-all"
                    checked={selectedAttributes.length === [...Object.values(groupedAttributes.grouped).flat(), ...groupedAttributes.ungrouped].length}
                    onChange={handleSelectAll}
                  />
                  <label
                    htmlFor="select-all"
                    className="mb-0 fw-bold"
                    style={{ cursor: 'pointer', flex: 1, fontSize: '14px' }}
                  >
                    Select All
                  </label>
                </div>
              </div>
              <div 
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                  alignItems: 'start'
                }}
              >
                {/* Grouped Attributes */}
                {Object.entries(groupedAttributes.grouped).map(([groupName, attrs]) => (
                  <div key={groupName} className="mb-3 w-100" >
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '14px', borderBottom: '2px solid #007bff', paddingBottom: '6px' }}>
                      {groupName}
                    </h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {attrs.map((attr, index) => renderAttributeCheckbox(attr, index))}
                    </div>
                  </div>
                ))}

                {/* Ungrouped Attributes */}
                {groupedAttributes.ungrouped.length > 0 && (
                  <div>
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '14px', borderBottom: '2px solid #007bff', paddingBottom: '6px' }}>
                      Other
                    </h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {groupedAttributes.ungrouped.map((attr, index) => renderAttributeCheckbox(attr, index))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <p className="mb-1">No attributes available</p>
              <small>Select child models to see available attributes</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
}